import { Collapse } from '@fortune-ui'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { Box } from '@pets-ui/Box/Box'
import { Container } from '@pets-ui/Container/Container'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowDownLarge, ArrowUpLarge } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { type PropsWithChildren, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom-v5'

const styles = stylex.create({
  sidebarContainer: {
    overflow: 'hidden',
    userSelect: 'none',
  },
  menuParent: {
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'space-between',
  },
  childItem: (active: boolean) => ({
    color: {
      'default': colors[`grey${active ? '900' : '600'}`],
      ':hover': colors.grey900,
    },
  }),
  logoutItem: {
    cursor: 'pointer',
  },
})

interface LayoutMyAccountProps {
  campaignCode: string | null
  basePath: string
  onLogout: () => void
}

export const LayoutMyAccount = ({ campaignCode, basePath, onLogout, children }: PropsWithChildren<LayoutMyAccountProps>) => {
  const isMobile = useResponsive('wordpressMobile')
  const { destructuredData: campaignMember } = useApiCampaignMember({ code: campaignCode ?? undefined })
  const { pathname } = useLocation()
  const orderPath = `${basePath}/my-account/order`
  const isOrderActive = pathname.startsWith(orderPath)
  const [isOrderMenuOpen, setIsOrderMenuOpen] = useState(isOrderActive)

  if (isMobile) {
    return (
      <Box p={4} backgroundColor={colors.grey100}>
        {children}
      </Box>
    )
  }

  return (
    <Box backgroundColor={colors.grey100} minHeight="100vh">
      <Container maxWidth={1144}>
        <HStack gap={5} p={5} flexWrap="nowrap">
          <Box flexShrink={0} width={200}>
            <Box borderRadius={8} backgroundColor={colors.white} overflow="hidden" rootStyle={styles.sidebarContainer}>
              <Box p={4}>
                <Text variant="subtitle" color={colors.grey600}>
                  {campaignMember?.user_name ?? ''}
                </Text>
              </Box>

              <HStack p={4} rootStyle={styles.menuParent} onClick={() => setIsOrderMenuOpen(prev => !prev)}>
                <Text variant="subtitle" color={isOrderActive ? colors.grey900 : colors.grey600}>
                  我的訂單
                </Text>
                {isOrderMenuOpen
                  ? <ArrowUpLarge color={colors.grey500} fontSize={20} />
                  : <ArrowDownLarge color={colors.grey500} fontSize={20} />}
              </HStack>

              <Collapse in={isOrderMenuOpen}>
                <Box py={3} backgroundColor={colors.grey50}>
                  <NavLink to={orderPath} style={{ textDecoration: 'none' }}>
                    <Box py={2} px={4}>
                      <Text variant="body" rootStyle={styles.childItem(isOrderActive)}>
                        一般訂單
                      </Text>
                    </Box>
                  </NavLink>
                </Box>
              </Collapse>

              <Box p={4} rootStyle={styles.logoutItem} onClick={onLogout}>
                <Text variant="subtitle" color={colors.grey600}>
                  登出
                </Text>
              </Box>
            </Box>
          </Box>
          <Box flex="1" minWidth={0}>
            {children}
          </Box>
        </HStack>
      </Container>
    </Box>
  )
}
